<template>
    <metainfo>
        <template #title="{ content }">{{ content }} - Streamforge</template>
    </metainfo>

    <notifications class="px-5 py-3" />

    <div class="relative overflow-hidden flex flex-col h-screen w-screen">
        <!-- The login page carries its own, larger notice. -->
        <LegacyRetirementBanner v-if="$route.name !== 'login'" />
        <router-view />

        <CreatorModal />
        <ParticipantModal />
        <AddCreatorToModal />
    </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import * as Sentry from '@sentry/vue'
import { z } from 'zod'
import { mapGetters } from 'vuex'
import ParticipantModal from '@/partials/modals/ParticipantModal.vue'
import CreatorModal from '@/partials/modals/CreatorModal.vue'
import AddCreatorToModal from '@/partials/modals/AddCreatorToModal.vue'
import LegacyRetirementBanner from '@/components/LegacyRetirementBanner.vue'
// import { updateQuotaFromHeaders } from '@/hooks/useQuotaUpdater'
import store from '@/store'
import { initIntercom } from './plugins/intercom'
import { setUserId } from './utils/analytics'
import { onMounted, onBeforeUnmount } from 'vue'

export default {
    components: {
        LegacyRetirementBanner,
        ParticipantModal,
        CreatorModal,
        AddCreatorToModal,
    },

    provide() {
        return {
            axios: this.$axios,
            filters: this.$filters,
        }
    },

    computed: {
        ...mapGetters(['tokens', 'isAuthenticated', 'self', 'selfActiveOrganization']),
        query() {
            return this.$route.query
        },
    },

    watch: {
        isAuthenticated(newValue) {
            if (newValue) {
                // Setup Axios
                this.bootstrapAxios()
                this.getSelf()
            } else {
                this?.$intercom?.shutdown()
            }
        },

        tokens(newValue, oldValue) {
            if (newValue.core !== oldValue.core) {
                // Setup Axios so we are sending auth header
                this.bootstrapAxios()
                this.getSelf()
            }
        },

        async selfActiveOrganization(newValue, oldValue) {
            if (!newValue || newValue?.id === oldValue?.id) return

            // console.log(`selfActiveOrganization`, { newValue, oldValue })
            this.bootstrapAxios()
            await this.getSelf()

            // I hate this but couldn't figure out a better way to do it
            // eslint-disable-next-line no-prototype-builtins
            if (this.$route.params.hasOwnProperty('organization_id')) {
                this.$router.push({
                    params: {
                        organization_id: newValue.id,
                    },
                })
            }

            // Setup Axios so we are sending active organization header
            // if (newValue && !oldValue || newValue.id !== oldValue.id) {
            //     console.log(`Bootstrapping axios`)
            //     this.bootstrapAxios()
            // }
        },

        // Clear creator stack when navigating to different pages
        '$route.path'() {
            // Clear the creator modal stack when the route path changes
            // This prevents orphaned stacks when navigating away
            if (this.$store.getters.hasStackedCreators) {
                this.$store.commit('modal:clearStack')
            }
        },

        // Watching query params for CreatorProfileModal
        query(newQuery, oldQuery) {
            if (!newQuery && _.size(newQuery) <= 0) {
                return
            }

            const querySchema = z.object({
                modal: z.enum(['creator']),
                platform_id: z.string(),
                platform_user_id: z.string(),
            })

            const queryValidation = querySchema.safeParse(newQuery)

            if (queryValidation.error) {
                // Close creator modal if query doesn't match
                if (this.$store.getters.hasStackedCreators) {
                    this.$store.commit('modal:clearStack')
                } else if (this.$store.getters.modals.creator.is_open) {
                    this.$store.commit('modal:close', 'creator')
                }
                return
            }

            // Check if this is the same creator profile (just query param updates like tab changes)
            const isSameCreator = oldQuery
                && oldQuery.modal === newQuery.modal
                && oldQuery.platform_id === newQuery.platform_id
                && oldQuery.platform_user_id === newQuery.platform_user_id

            // Only clear stack if it's a different creator or initial open
            // This prevents clearing on tab navigation or other query param changes
            if (!isSameCreator) {
                // URL-based opens start fresh (for sharing/bookmarking)
                // Clear stack first, then open
                this.$store.commit('modal:clearStack')

                this.$store.commit('modal:setData', {
                    modal_id: 'creator',
                    data: {
                        is_open: true,
                        platform_id: newQuery.platform_id,
                        platform_user_id: newQuery.platform_user_id,
                    },
                })
            }
        },
    },

    created() {
        window.addEventListener('storage', this.handleUnlockStorageEvent)

        if (this.isAuthenticated) {
            this.bootstrapAxios()
            this.getSelf()
        }

        // if (this.$route.hash) {
        //     const unhashedHash = this.$route.hash.slice(1)
        //     const allowedTypes = ['modal:creator']
        //     const hashParts = unhashedHash.split('|')

        //     if (hashParts && hashParts.length > 1) {
        //         const typeString = hashParts.shift()

        //         if (typeString && allowedTypes.includes(typeString)) {
        //             const [type, subtype] = typeString.split(':')

        //             const attributes = _.reduce(hashParts, (hashPart) => {
        //                 const [key, value] = hashPart.split(':')
        //                 result[key] = value
        //                 return result
        //             }, {})

        //             if (type === 'modal') {
        //                 this.$store.commit('modal:setData', {
        //                     modal_id: subtype,
        //                     data: {
        //                         is_open: true,
        //                         ...attributes
        //                     },
        //                 })
        //             }
        //         }
        //     }
        // }
    },

    beforeUnmount() {
        window.removeEventListener('storage', this.handleUnlockStorageEvent)
    },

    methods: {
        async getSelf() {
            this?.$intercom?.shutdown()

            return this.$axios
                .get('/me', {
                    noCancel: true,
                })
                .then(async ({ data }) => {
                    this.$store.commit('self:operator', {
                        operator: data.payload.operator,
                    })

                    this.$store.commit('self:organizations', {
                        organizations: data.payload.organizations,
                    })

                    this.$store.commit('self:subscriptionTier', {
                        subscriptionTier: data.payload?.organizationSettings?.tier,
                    })

                    this.$store.commit('self:discoveryInstagramAudienceFilters', {
                        discoveryInstagramAudienceFilters: data.payload?.organizationSettings?.discovery_instagram_audience_filters === true,
                    })

                    this.$store.commit('self:hasSubscription', {
                        hasSubscription: data.payload?.hasSubscription === true,
                    })

                    this.$store.commit('self:defaultPlatformId', {
                        defaultPlatformId: data.payload?.organizationSettings?.default_platform_id || 'instagram',
                    })

                    if (data.payload.operator && import.meta.env.MODE === 'production') {
                        const hasSubscription = _.filter(data?.payload?.organizations, organization => {
                            return organization?.subscriptions?.length > 0
                        }).length > 0

                        initIntercom(this, data.payload, hasSubscription)
                    }

                    if (data.payload.operator && data.payload.organizations && data.payload.organizations.length) {
                        const isSfStaff =
                            !!_.find(data.payload.organizations, (organization) => organization.type === 'admin') ||
                            data.payload.operator.email.includes('@streamforge.com')

                        // Don't log sessions of staff
                        if (import.meta.env.MODE === 'production') {
                            if (!isSfStaff) {
                                await this.$logRocket.init('cxu3yt/client-dashboard')
                                await this.$logRocket.identify(data.payload.operator.id, {
                                    name: `${data.payload.operator.first_name} ${data.payload.operator.last_name}`,
                                    email: data.payload.operator.email,
                                })

                                // this.$openReplayTracker.start()
                                // this.$openReplayTracker.setUserID(data.payload.operator.id)
                                // this.$openReplayTracker.setMetadata('email', data.payload.operator.email)
                                // this.$openReplayTracker.setMetadata('joined_at', data.payload.operator.joined_at)
                            }

                            // this.$intercom.show()
                        }

                        // Only identify in PostHog if it's initialized (production mode)
                        if (import.meta.env.MODE === 'production') {
                            try {
                                this.$posthog.identify(data.payload.operator.id, {
                                    email: data.payload.operator.email,
                                    name: `${data.payload.operator.first_name} ${data.payload.operator.last_name}`,
                                    created_at: data.payload.operator.joined_at,
                                })
                            } catch (error) {
                                console.error('Posthog identification failed', error)
                            }
                        }

                        Sentry.setUser({
                            id: data.payload.operator.id,
                            email: data.payload.operator.email,
                            name: `${data.payload.operator.first_name} ${data.payload.operator.last_name}`,
                            organizations: JSON.stringify(data.payload.organizations),
                        })

                        // Set user_id for session on GA4
                        if (data?.payload?.operator?.id) {
                            setUserId(data.payload.operator.id)
                        }

                        const hasExistingActiveOrganization = !!this.selfActiveOrganization

                        const isExistingActiveOrganizationPermitted =
                            hasExistingActiveOrganization &&
                            _.find(
                                data.payload.organizations,
                                (organization) => organization.id === this.selfActiveOrganization.id,
                            )

                        /* if (!hasExistingActiveOrganization) {
                            console.log(`No existing active organization, trigger set first`)
                        } else if (!isExistingActiveOrganizationPermitted) {
                            console.log(`Existing active orgnization is stale, triggering set first`)
                        } else {
                            console.log(`Existing active organization is fine, skip`)
                        } */

                        if (!hasExistingActiveOrganization || !isExistingActiveOrganizationPermitted) {
                            this.$store.commit('self:activeOrganizationId', {
                                id: _.first(data.payload.operator.organizations).id,
                            })
                        }
                    }

                    this.$store.commit('permissions:setup', data.payload.permissions)
                    this.$store.commit('permissions:isLoaded', true)

                    // Set the posthog and intercom organization
                    if (this.selfActiveOrganization) {
                        try {
                            // Only set PostHog group in production mode
                            if (import.meta.env.MODE === 'production') {
                                this.$posthog.group('organization', this.selfActiveOrganization?.id, {
                                    name: this.selfActiveOrganization?.name,
                                    type: this.selfActiveOrganization?.type,
                                })
                            }
                            if (this.$intercom?.update) {
                                this.$intercom.update({
                                    company: {
                                        id: this.selfActiveOrganization?.id,
                                        name: this.selfActiveOrganization?.name,
                                    }
                                })
                            }
                        } catch (error) {
                            console.error('Error syncing posthog and/or intercom organization', error)
                        }
                    }

                    if (data.payload.quotas) {
                        const quotaMap = {
                            'email-reveal': 'emailReveals',
                            'discovery-creators': 'discovery',
                            'export-creators': 'exports',
                            'profile-unlock': 'profileUnlocks',
                            'report-generation': 'reportGeneration',
                        }
                        let quotaResetDate = null
                        data.payload.quotas.forEach((quota) => {
                            store.commit('quota:update', {
                                type: quotaMap[quota.type],
                                key: 'allocated',
                                value: quota.allocated_quota
                            })
                            store.commit('quota:update', {
                                type: quotaMap[quota.type],
                                key: 'used',
                                value: quota.used_quota
                            })
                            store.commit('quota:update', {
                                type: quotaMap[quota.type],
                                key: 'remaining',
                                value: quota.allocated_quota - quota.used_quota
                            })

                            if (quotaResetDate == null || quota.ended_at < quotaResetDate) {
                                quotaResetDate = quota.ended_at
                            }

                        })

                        store.commit('quota:resetDate', { newResetDate: quotaResetDate })
                    }

                    return true
                })
                .catch((error) => {
                    console.error(error)

                    let message = 'Failed to find your account info.'
                    const apiMessage = _.get(error, 'response.data.payload.message')
                    if (apiMessage) message = apiMessage

                    this.$notify({
                        group: 'global',
                        type: 'alert',
                        title: 'Error - Account data not found',
                        text: message,
                        duration: 10000,
                        speed: 1000,
                    })
                })
        },

        bootstrapAxios() {
            this.$axios.defaults.headers.Authorization = `Bearer ${this.tokens.core}`

            if (this.selfActiveOrganization && this.selfActiveOrganization.id) {
                this.$axios.defaults.headers['x-active-organization-id'] = this.selfActiveOrganization.id

                // Make sure we remove this if no active organization is set
            } else {
                delete this.$axios.defaults.headers['x-active-organization-id']
            }

            this.$axios.interceptors.response.use(
                (response) => {
                    // If request using a bearer token fails, it has expired, and log them out
                    if (response.status === 401) {
                        // Save the URL the user was trying to access so we can redirect to it after
                        // So long as it is not an admin route
                        if (this.$route.fullpath) {
                            this.$store.commit('authentication:redirect', this.$route.fullPath)
                        } else {
                            this.$store.commit('authentication:redirect', null)
                        }

                        // Logout
                        this.$store.commit('authentication:logout')

                        // Clear user info
                        this.$store.commit('self:clear')

                        const isWithinDashboardRoot = _.find(this.$route.matched, (matchedRoute) => {
                            return matchedRoute.name === 'dashboard-root'
                        })

                        if (isWithinDashboardRoot) {
                            this.$router.push({ name: 'login' })
                        } else {
                            window.location.reload()
                        }
                    }

                    const typeMap = {
                        'email-reveal': 'emailReveals',
                        'discovery-creators': 'discovery',
                        'export-creators': 'exports',
                        'profile-unlock': 'profileUnlocks',
                        'report-generation': 'reportGeneration',
                    }

                    // Collect the quota info from the headers
                    const collected = {}

                    Object.entries(response.headers).forEach(([key, value]) => {
                        const match = key.match(/^x-quota-(used|allocated)-([a-z-]+)$/)
                        if (!match) return
                        // console.log("Response with a match:", response)
                        // console.log("Match is:", match)
                        // console.log("Matched with value:", value)
                        const [, kind, rawType] = match
                        const type = typeMap[rawType]

                        if(!type) return

                        if (!collected[type]) {
                            collected[type] = {}
                        }

                        collected[type][kind] = Number(value)
                    })

                    // Commit what we collected into the store
                    Object.entries(collected).forEach(([type, { used, allocated }]) => {
                        if (typeof used === 'number' && typeof allocated === 'number') {
                            store.commit('quota:update', {
                                type,
                                key: 'used',
                                value: used,
                            })
                            store.commit('quota:update', {
                                type,
                                key: 'remaining',
                                value: allocated - used,
                            })
                            store.commit('quota:update', {
                                type,
                                key: 'allocated',
                                value: allocated,
                            })
                            // console.log(store.state.quota)
                        }
                    })

                    return response
                },

                (error) => Promise.reject(error),
            )
        },

        handleUnlockStorageEvent(e) {
            if (e.key === 'lastUnlock' && e.newValue) {
                try {
                const data = JSON.parse(e.newValue)
                if (!data?.key) return

                // Only update the store locally, don’t hit the API again
                const isAlreadyUnlocked = this.$store.getters.unlocks[data.key]?.state === 'revealed'
                const [quotaType, ...keyIds] = data.key.split(':')

                if (!isAlreadyUnlocked) {
                    this.$store.dispatch('reveal', {
                        quotaType,
                        keyIds,
                    })
                }
                } catch (err) {
                    console.error('Failed to parse unlock sync event', err)
                }
            }
        },
    },
}
</script>
